/**
 * 버튼 생김새의 단일 기준.
 *
 * 두 번의 지적을 그대로 반영한 결과다.
 *
 * 1. 글자만 있는 버튼은 본문 텍스트와 구분되지 않는다 →
 *    **누르는 것에는 예외 없이 테두리와 여백으로 면을 준다.**
 * 2. 전부 흑백이라 주 동작과 보조 동작이 구분되지 않는다 →
 *    **주 동작에만 주색(teal)을 쓴다.** 보조는 흰 면 + 회색 테두리로 남긴다.
 *
 * 주색은 1차 프로젝트(`hanjung-driver`)의 `kTealColor`(`#0D9488`)를 그대로 쓴다.
 * 색 정의는 `tailwind.config.ts` 의 `primary` 스케일.
 *
 * 여기 한 곳만 고치면 담당자 화면·기사 화면·공용 조각이 함께 바뀐다. 페이지 쪽
 * `_components/ui.tsx` 에 두면 `DateNavigator` 같은 base 조각이 가져다 쓸 수 없다
 * (base → pages 방향 의존은 만들지 않는다).
 *
 * 종류는 셋이다. 테두리 없는 "조용한" 변형은 두지 않는다 — 그게 곧 예전의
 * 글자만 있는 버튼이라, 상단바든 표 안이든 다시 안 보이게 된다.
 *
 * - `primary`   : 그 화면에서 일을 확정하는 **단 하나**의 동작 (등록·저장·검증 완료)
 * - `secondary` : 그 밖의 모든 동작 (목록·보류·연결·전일/다음일·로그아웃). 기본값
 * - `selected`  : 여러 보기 중 **지금 고른 것** (기간 프리셋 등). 동작이 아니라 상태다
 *
 * 한 화면에 주색으로 **채운** 버튼이 둘 이상이면 위계가 무너진 것이다 — 하나만 남기고 내린다.
 * `selected` 는 면을 채우지 않고 테두리와 글자에만 주색을 써서 주 동작과 경쟁하지 않는다.
 * CLAUDE.md §주색 의 "지금 여기 / 고른 것" 쪽 용도다 (탭·사이드바 현재 메뉴와 같은 자리).
 */

export type ButtonVariant = "primary" | "secondary" | "selected";
export type ButtonSize = "default" | "small" | "compact";

const BASE =
  "inline-flex items-center justify-center whitespace-nowrap rounded-md border transition-colors";

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    "border-primary-600 bg-primary-600 font-medium text-white " +
    "hover:border-primary-700 hover:bg-primary-700 " +
    "disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-400",
  secondary:
    "border-gray-300 bg-white text-gray-700 hover:border-gray-900 hover:text-gray-900 " +
    "disabled:border-gray-200 disabled:bg-white disabled:text-gray-300",
  selected:
    "border-primary-600 bg-white font-medium text-primary-700 " +
    "hover:border-primary-700 hover:text-primary-800 " +
    "disabled:border-gray-200 disabled:bg-white disabled:text-gray-300",
};

/**
 * **버튼 높이는 앱에 둘뿐이다 — 26px 과 18px.**
 *
 * 예전에는 `default` 가 34px(주 동작은 38px)이라, 같은 화면 안에서도 머리말 버튼만
 * 혼자 두 배 가까이 컸다. 밀도를 `조밀`로 내려도 버튼이 든 줄만 두꺼워지는 것과 같은 문제다
 * (`index.css` 의 표 안 버튼 주석 참고). 기준은 **기준정보 화면의 `신규 등록` 버튼**(26px)이다 —
 * 담당자가 가장 자주 보는 버튼이 그것이고, 표 액션 줄(`.hj-table-actions`)도 이미 같은 값이다.
 *
 * `default` 와 `small` 은 **높이가 같고 좌우 여백만 다르다.** 여백이 있는 자리(머리말·모달 바닥)는
 * 가로로 조금 넓게, 줄이 빡빡한 자리(필터바 옆·표 옆)는 좁게 둔다.
 */
const SIZE: Record<ButtonSize, string> = {
  /** 머리말·모달 바닥. 테두리까지 26px */
  default: "px-3 py-1 text-xs",
  /** 필터바 옆·표 옆. 높이는 `default` 와 같고 좌우만 좁다 */
  small: "px-2.5 py-1 text-xs",
  /**
   * 줄 높이를 한 톨도 밀면 안 되는 자리 — 표 안, 그리고 작은 라벨(`text-xs`) 옆.
   * 테두리까지 18px 로 본문 글자 줄높이(20px)보다 낮다.
   * 표 안 버튼은 `index.css` 가 같은 값을 한 곳에서 걸어 주므로 따로 지정하지 않아도 된다.
   */
  compact: "px-2 py-0 text-[11px] leading-4",
};

/** 주 동작은 좌우로만 한 단계 넓게 — 높이는 보조 버튼과 같게 두어 줄이 들쭉날쭉해지지 않는다 */
const PRIMARY_SIZE: Record<ButtonSize, string> = {
  default: "px-3.5 py-1 text-xs",
  small: "px-2.5 py-1 text-xs",
  compact: "px-2 py-0 text-[11px] leading-4",
};

export function buttonClass(
  variant: ButtonVariant = "secondary",
  size: ButtonSize = "default"
): string {
  const sizeClass = variant === "primary" ? PRIMARY_SIZE[size] : SIZE[size];
  return `${BASE} ${VARIANT[variant]} ${sizeClass}`;
}
